/**
 * Print API client — manual label printing from the WorkItem dashboard.
 *
 * Posts to sm4rt-api `POST /api/print`, which builds the job envelope and
 * publishes it to Service Bus for the on-site print agent to render + print
 * (see sm4rt-print-agent). Site/role are resolved from sm4rt-api config
 * defaults, so the client only needs the work item id.
 */

import type {
  PrintLabelRequest,
  PrintLabelResult,
  PrintLane,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export type { PrintLabelResult, PrintLane };

/** POST a print body and unwrap the API's error message on failure. */
async function postPrint(
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<PrintLabelResult> {
  const response = await fetch(`${API_BASE_URL}/print`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Print failed: ${response.status} ${response.statusText}`,
    }));
    throw new Error(errorData.error || `Print failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Print the repair-ticket ("job") label for a work item.
 *
 * `role` addresses one of the two label lanes — the workshop printer upstairs
 * or the counter printer downstairs (see `config/printLanes.ts`). Omitting it
 * lets sm4rt-api resolve its configured default, which is the counter. The site
 * is always the API's default; a role belonging to another site would address
 * the wrong shop's printer.
 *
 * Resolves on a queued/sent result; rejects with the API error message.
 */
export async function printRepairTicketLabel(
  workItemId: string,
  options: { role?: string; copies?: number } = {},
  signal?: AbortSignal,
): Promise<PrintLabelResult> {
  return postPrint(
    {
      workItemId,
      document: 'repair-ticket-label',
      ...(options.role ? { role: options.role } : {}),
      // Omitted at 1 so the request stays identical to what it was before
      // copies were offered; sm4rt-api defaults it to 1 anyway.
      ...(options.copies && options.copies > 1 ? { copies: options.copies } : {}),
    },
    signal,
  );
}

/**
 * Print the A4 job ticket sheet for a work item — the hand-fill check-in form
 * that used to be printed from the in-app preview page.
 *
 * Unlike the labels this takes no lane: the sheet is A4 and only the office
 * laser can render it, so sm4rt-api resolves the lane from the document. The
 * work item's linked check-in session (if any) is resolved server-side, so a
 * device whose inspection was captured digitally prints filled rather than
 * blank — one page per device, exactly as the preview page renders it.
 */
export async function printTicketSheet(
  workItemId: string,
  options: { copies?: number } = {},
  signal?: AbortSignal,
): Promise<PrintLabelResult> {
  return postPrint(
    {
      workItemId,
      document: 'job-ticket-sheet',
      ...(options.copies && options.copies > 1 ? { copies: options.copies } : {}),
    },
    signal,
  );
}

/**
 * Print an A4 tax invoice to the office laser.
 *
 * Takes the invoice *number* (the human one, e.g. "2611"), not the RepairShopr
 * id — that is what the work item's invoice summary carries and what
 * sm4rt-api's invoice endpoints key on. The lane is resolved from the document
 * server-side, as for the ticket sheet.
 *
 * This is NOT `invoicesApi.printInvoice`, which queues the print inside
 * RepairShopr and comes out of whatever RS is pointed at. This one goes through
 * our own agent onto our own paper.
 */
export async function printInvoiceDocument(
  invoiceNumber: string,
  signal?: AbortSignal,
): Promise<PrintLabelResult> {
  return postPrint(
    { invoiceNumber, document: 'invoice-techsolvers' },
    signal,
  );
}

/**
 * Print an ad-hoc label (PIN / password / preformatted) to the site's default
 * label printer. The body carries the label `document` + `data` directly (no
 * work item). Resolves on a queued/sent result; rejects with the API error.
 */
export async function printLabel(
  request: PrintLabelRequest,
  signal?: AbortSignal,
): Promise<PrintLabelResult> {
  return postPrint(request as unknown as Record<string, unknown>, signal);
}

/**
 * The printer lanes this user may address, from `GET /api/print/lanes`.
 *
 * Distinct from `printersApi.fetchPrinters`, which is the admin CRUD list and
 * is admin-gated: this one is open to any signed-in user, because picking
 * between the counter label printer and the workshop one is not an admin act.
 * It carries only the lane (`siteId` + `role`) and its engine — never the
 * printer's address, which a client has no use for.
 */
export async function fetchPrintLanes(signal?: AbortSignal): Promise<PrintLane[]> {
  const response = await fetch(`${API_BASE_URL}/print/lanes`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to load printers: ${response.status} ${response.statusText}`,
    }));
    throw new Error(errorData.error || `Failed to load printers: ${response.status}`);
  }

  const data = await response.json();
  return data.items ?? [];
}
